// src/httpClient.ts
class HttpError extends Error {
  status;
  response;
  constructor(status, response, message) {
    super(message || `HTTP Error: ${status}`);
    this.status = status;
    this.response = response;
    this.name = "HttpError";
  }
}

class HttpClient {
  authToken = null;
  defaultTimeout;
  constructor(defaultTimeout = 5000) {
    this.defaultTimeout = defaultTimeout;
  }
  setAuthToken(token) {
    this.authToken = token;
  }
  clearAuthToken() {
    this.authToken = null;
  }
  async request(url, options = {}, timeout) {
    const requestTimeout = timeout ?? this.defaultTimeout;
    const controller = new AbortController;
    const timer = setTimeout(() => controller.abort(), requestTimeout);
    const headers = new Headers(options.headers || {});
    if (this.authToken) {
      headers.set("Authorization", `Bearer ${this.authToken}`);
    }
    const requestOptions = {
      ...options,
      headers,
      signal: controller.signal
    };
    try {
      const response = await fetch(url, requestOptions);
      clearTimeout(timer);
      const contentType = response.headers.get("Content-Type") || "";
      let data;
      if (contentType.includes("application/json")) {
        if (response.status === 204) {
          data = null;
        } else {
          const text = await response.text();
          try {
            data = text ? JSON.parse(text) : null;
          } catch (parseError) {
            console.error("Failed to parse JSON response:", parseError, "Body:", text);
            throw new HttpError(response.status, text, `HTTP Error: ${response.status} - Invalid JSON response`);
          }
        }
      } else if (response.status !== 204) {
        data = await response.text();
      } else {
        data = null;
      }
      if (!response.ok) {
        throw new HttpError(response.status, data, `HTTP Error: ${response.status}`);
      }
      return data;
    } catch (error) {
      clearTimeout(timer);
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Request timed out");
      }
      if (error instanceof HttpError) {
        throw error;
      }
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Request failed: ${errorMessage}`);
    }
  }
  async get(url, options = {}, timeout) {
    return this.request(url, { ...options, method: "GET" }, timeout);
  }
  async post(url, body, options = {}, timeout) {
    const headers = new Headers({
      "Content-Type": "application/json",
      ...options.headers || {}
    });
    return this.request(url, {
      ...options,
      method: "POST",
      headers,
      body: JSON.stringify(body)
    }, timeout);
  }
  async put(url, body, options = {}, timeout) {
    const headers = new Headers({
      "Content-Type": "application/json",
      ...options.headers || {}
    });
    return this.request(url, {
      ...options,
      method: "PUT",
      headers,
      body: JSON.stringify(body)
    }, timeout);
  }
  async patch(url, body, options = {}, timeout) {
    const headers = new Headers({
      "Content-Type": "application/json",
      ...options.headers || {}
    });
    return this.request(url, {
      ...options,
      method: "PATCH",
      headers,
      body: JSON.stringify(body)
    }, timeout);
  }
  async del(url, options = {}, timeout) {
    return this.request(url, { ...options, method: "DELETE" }, timeout);
  }
  async delete(url, options = {}, timeout) {
    return this.del(url, options, timeout);
  }
}
export {
  HttpError,
  HttpClient
};
